import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Mail, Sparkles, ArrowRight, X, Globe, Sun, Moon, Check } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTheme } from "@/components/theme-provider";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { useLocale } from "@/hooks/use-locale";
import { useTranslations } from "@/hooks/use-translations";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { TopBarSettings } from "@/routes/_authenticated/admin/top-bar";

const STORAGE_KEY = "coderlity:topbar-dismissed-v1";

const DEFAULTS: TopBarSettings = {
  enabled: true,
  phone: "+966 55 975 8082",
  email: "hello@coderlity.com",
  promo_text: "New: AI Automation Suite is live —",
  promo_cta_label: "Explore",
  promo_cta_to: "/services",
  support_label: "Support",
  support_to: "/support",
  cta_label: "Get a quote",
  cta_to: "/contact",
  language_label: "EN",
};

const LOCALE_FLAGS: Record<string, string> = {
  en: "🇬🇧", "en-us": "🇺🇸", "en-gb": "🇬🇧",
  ar: "🇸🇦", bn: "🇧🇩", hi: "🇮🇳",
  zh: "🇨🇳", "zh-cn": "🇨🇳", "zh-tw": "🇹🇼",
  fr: "🇫🇷", es: "🇪🇸", de: "🇩🇪", it: "🇮🇹",
  pt: "🇵🇹", "pt-br": "🇧🇷", ja: "🇯🇵", ko: "🇰🇷",
  ru: "🇷🇺", tr: "🇹🇷", nl: "🇳🇱", pl: "🇵🇱",
  ur: "🇵🇰", fa: "🇮🇷", id: "🇮🇩", ms: "🇲🇾",
  th: "🇹🇭", vi: "🇻🇳", sv: "🇸🇪", no: "🇳🇴",
  da: "🇩🇰", fi: "🇫🇮", el: "🇬🇷", he: "🇮🇱",
};

function flagFor(code: string): string {
  const c = code.toLowerCase();
  if (LOCALE_FLAGS[c]) return LOCALE_FLAGS[c];
  const base = c.split("-")[0];
  if (LOCALE_FLAGS[base]) return LOCALE_FLAGS[base];
  // Fallback: derive regional indicator from 2-letter region or base code
  const region = (c.split("-")[1] ?? base).slice(0, 2).toUpperCase();
  if (/^[A-Z]{2}$/.test(region)) {
    return String.fromCodePoint(...[...region].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
  }
  return "🌐";
}

export function TopBar() {
  const [visible, setVisible] = useState(true);
  const { theme, toggle } = useTheme();
  const { data } = useSiteSettings<Partial<TopBarSettings>>("top_bar");
  const cfg: TopBarSettings = { ...DEFAULTS, ...(data ?? {}) };
  const { t } = useTranslations();
  const { code: localeCode, setLocale, locales } = useLocale();
  const activeLocale = locales.find((l) => l.code === localeCode);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY) === "1") setVisible(false);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (cfg.enabled === false || !visible) return null;
  const telHref = `tel:${cfg.phone.replace(/[^+\d]/g, "")}`;

  return (
    <div className="relative overflow-hidden border-b border-border/60 bg-gradient-brand text-white min-h-[36px]">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs lg:px-8">
            {/* Left: contact strip */}
            <div className="hidden items-center gap-5 md:flex">
              <a href={telHref} className="inline-flex items-center gap-1.5 opacity-90 transition hover:opacity-100">
                <Phone className="h-3.5 w-3.5" />
                <span>{cfg.phone}</span>
              </a>
              <a href={`mailto:${cfg.email}`} className="inline-flex items-center gap-1.5 opacity-90 transition hover:opacity-100">
                <Mail className="h-3.5 w-3.5" />
                <span>{cfg.email}</span>
              </a>
            </div>

            {/* Center: promo */}
            <div className="flex min-w-0 flex-1 items-center justify-center gap-2 text-center">
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-medium">
                {cfg.promo_text}{" "}
                <a
                  href={cfg.promo_cta_to}
                  className="inline-flex items-center gap-1 font-semibold underline-offset-4 hover:underline"
                >
                  {cfg.promo_cta_label} <ArrowRight className="h-3 w-3" />
                </a>
              </span>
            </div>

            {/* Right: utility links */}
            <div className="hidden items-center gap-4 md:flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 opacity-90 transition hover:bg-white/10 hover:opacity-100"
                    aria-label="Change language"
                  >
                    <span className="text-sm leading-none">
                      {activeLocale ? flagFor(activeLocale.code) : <Globe className="h-3.5 w-3.5" />}
                    </span>
                    <span>
                      {activeLocale?.native_name ?? activeLocale?.name ?? cfg.language_label}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-52">
                  <DropdownMenuLabel className="text-xs">Language</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {locales.length === 0 && (
                    <DropdownMenuItem disabled>No languages enabled</DropdownMenuItem>
                  )}
                  {locales.map((l) => (
                    <DropdownMenuItem
                      key={l.code}
                      onSelect={() => setLocale(l.code)}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-base leading-none">{flagFor(l.code)}</span>
                        <span>{l.native_name ?? l.name}</span>
                        {l.native_name && l.name && l.native_name !== l.name && (
                          <span className="text-xs text-muted-foreground">({l.name})</span>
                        )}
                      </span>
                      {l.code === localeCode && <Check className="h-3.5 w-3.5 opacity-80" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                type="button"
                onClick={toggle}
                aria-label="Toggle theme"
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 opacity-90 transition hover:bg-white/10 hover:opacity-100"
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                <span>{theme === "dark" ? t("common.light", "Light") : t("common.dark", "Dark")}</span>
              </button>
              <a href={cfg.support_to} className="opacity-90 transition hover:opacity-100">
                {t("nav.support", cfg.support_label)}
              </a>
              <a href={cfg.cta_to} className="font-semibold opacity-95 transition hover:opacity-100">
                {t("nav.get_quote", cfg.cta_label)}
              </a>
              <button
                onClick={dismiss}
                aria-label="Dismiss announcement"
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-md opacity-80 transition hover:bg-white/10 hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Mobile dismiss */}
            <button
              onClick={dismiss}
              aria-label="Dismiss announcement"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md opacity-80 transition hover:bg-white/10 md:hidden"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
    </div>
  );
}
